<template>
  <panel-layout
    id="content-template-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="contentTemplate" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ contentTemplate.isNewRecord ? $t('pages.serviceAutomationTemplate.edit.new') : contentTemplate.name }}
          </h2>
          <div v-if="!contentTemplate.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200">
              #{{ contentTemplate.idContentTemplate }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="contentTemplate" ref="form" class="h-full">
      <o-scroll class="h-full">
        <div class="w-full px-6 py-4">
          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.title')">
              <text-field v-model="contentTemplate.name" :label="$t('general.title')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group class="w-full" :label="$t('general.enabled')" :info="$t('pages.serviceAutomationTemplate.edit.enabledInfo')">
              <toggle-field v-model="contentTemplate.enabled" :label="$t('general.enabled')" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" :rules="emailBodyHasContent ? 'required' : ''" class="w-full" :label="$t('pages.serviceAutomationTemplate.edit.emailSubject')" :info="$t('pages.serviceAutomationTemplate.edit.emailSubjectInfo')">
              <text-field v-model="contentTemplate.emailSubject" :label="$t('pages.serviceAutomationTemplate.edit.emailSubject')" :status="status" />
            </o-field-group>
          </div>

          <template v-if="contentTemplate.overrides.slots">
            <ContentTemplateSlot
              v-for="item in contentTemplate.overrides.slots"
              :key="item.id"
              v-model="item.value"
              :item="item"
              class="mb-6"
              :contentTemplate="contentTemplate"
            />
          </template>

          <template v-if="contentTemplate.overrides.blocks">
            <div
              v-for="block in contentTemplate.overrides.blocks"
              :key="block.id"
              class="border border-gray-200 dark:border-gray-800 rounded-lg mb-6 bg-gray-50 dark:bg-gray-900"
            >
              <div class="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-3 py-2">
                <h4 class="font-semibold text-gray-800 dark:text-gray-100">
                  {{ block.description }}
                </h4>
              </div>

              <div class="p-3">
                <ContentTemplateSlot
                  v-for="item in block.options"
                  v-model="item.value"
                  :key="item.id"
                  :item="item"
                  class="mb-6 last:mb-0"
                />
              </div>
            </div>
          </template>

          <div class="flex space-x-4 mb-6">
            <o-field-group class="w-full" :label="$t('pages.serviceAutomationTemplate.edit.smsBody')" :info="$t('pages.serviceAutomationTemplate.edit.smsBodyInfo')">
              <template #control>
                <UPopover v-if="smsPlaceholders.length" :content="{ side: 'top' }">
                  <UButton
                    :aria-label="$t('pages.serviceAutomationTemplate.edit.smsPlaceholders')"
                    icon="lucide:info"
                    size="xs"
                    color="info"
                    variant="ghost"
                    square
                  />
                  <template #content>
                    <div class="max-h-72 max-w-sm overflow-y-auto p-3 text-sm">
                      <p class="font-medium">
                        {{ $t('pages.serviceAutomationTemplate.edit.smsPlaceholders') }}
                      </p>
                      <p class="mt-1 text-gray-600 dark:text-gray-300">
                        {{ $t('pages.serviceAutomationTemplate.edit.smsPlaceholdersInfo') }}
                      </p>
                      <ul class="mt-2 space-y-1">
                        <li v-for="placeholder in smsPlaceholders" :key="placeholder">
                          <code v-text="formatPlaceholder(placeholder)" />
                        </li>
                      </ul>
                    </div>
                  </template>
                </UPopover>
              </template>
              <template #default="{ status }">
                <o-sms v-model="contentTemplate.smsBody" :status="status" class="w-full" />
              </template>
            </o-field-group>
          </div>

          <template v-if="contentTemplate.overrides.options">
            <ContentTemplateOption
              v-for="item in contentTemplate.overrides.options"
              v-model="item.value"
              :key="item.id"
              :item="item"
              class="mb-6"
            />
          </template>
        </div>
      </o-scroll>
    </o-form>

    <template #footer>
      <div v-if="contentTemplate">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />

    <Teleport :to="'#' + panel.extension">
      <div class="hidden md:block h-full w-full max-w-sm flex-shrink-0 pl-2 overflow-hidden relative">
        <div class="bg-white rounded-xl shadow-lg h-full w-full overflow-hidden dark:bg-gray-900">
          <div v-if="preview" class="h-full flex flex-col">
            <h3 class="p-4 text-lg font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-800">{{ preview.email.subject }}</h3>
            <div class="flex-1 overflow-hidden">
              <iframe
                :srcdoc="previewHtml"
                class="w-full h-full border-0"
                sandbox="allow-same-origin allow-scripts allow-popups"
              />
            </div>
          </div>
          <div v-else class="flex items-center justify-center h-full text-gray-500">
            {{ $t('general.preview') }}
          </div>
        </div>
      </div>
    </Teleport>
  </panel-layout>
</template>
<script setup lang="ts">
import debounce from 'lodash/debounce'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

// @ts-ignore
import ContentTemplateSlot from '@/components/contentTemplate/elements/Slot.vue'
// @ts-ignore
import ContentTemplateOption from '@/components/contentTemplate/elements/Option.vue'
import {
  TextField,
  ToggleField,
} from '@/components/fields'
import { OSms } from '@/components/elements'

import { ContentTemplateType } from '~/_api/models/contentTemplate'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const contentTemplate = ref<any>(null)
const errors = ref<any>(false)
const loading = ref(true)
const preview = ref<any>(null)
const previewHtml = computed<string | null>(() => preview.value?.email?.html ?? null)

const confirmDialog = ref<any>()
const form = ref<any>()

const emailBodyHasContent = computed(() => {
  const body = contentTemplate.value?.overrides?.slots
    ?.find((slot: any) => slot.name === 'body')?.value

  if (typeof body !== 'string') return Boolean(body)
  if (/<(img|video|audio|iframe|table|hr)\b/i.test(body)) return true

  const text = body
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u200B/g, '')

  return text.trim().length > 0
})

const smsPlaceholders = computed<string[]>(() => {
  const placeholders = contentTemplate.value?.placeholders?.text

  if (!Array.isArray(placeholders)) return []

  return placeholders.filter((placeholder: unknown): placeholder is string => (
    typeof placeholder === 'string' && placeholder.length > 0
  ))
})

const formatPlaceholder = (placeholder: string) => `{{ ${placeholder} }}`

const { unsavedChanges, snapshot } = useUnsavedChanges(contentTemplate)

const fetchPreview = debounce(async () => {
  try {
    preview.value = (await $api.contentTemplate.preview(contentTemplate.value)).data
  } catch (e) {
    console.error('Failed to generate preview:', e)
  }
}, 500)

watch(contentTemplate, () => {
  nextTick(() => {
    fetchPreview()
  })
}, { deep: true })

const save = async (close = false) => {
  if (!await form.value?.validate()) return false

  loading.value = true
  errors.value = false

  try {
    const result = (await $api.contentTemplate.save(contentTemplate.value)).data

    if (result) {
      contentTemplate.value = result
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.serviceAutomationTemplate.edit.success'),
        text: $i18n.t('pages.serviceAutomationTemplate.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('ContentTemplate', result)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    $notification.error({ errors: e?.errors })
    return false
  } finally {
    loading.value = false
  }
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      contentTemplate.value = (await $api.contentTemplate.getDefaults(ContentTemplateType.serviceAutomation)).data
    } else {
      contentTemplate.value = (await $api.contentTemplate.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })

// mounted
onMounted(() => {
  if (contentTemplate.value) {
    fetchPreview()
  }
})
</script>
